"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { loadTossPayments, ANONYMOUS } from "@tosspayments/tosspayments-sdk"
import { Header } from "@/components/header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { promptSummaries } from "@/lib/mock-data"
import { getPaymentReturnUrls } from "@/lib/payment-return-paths"
import { savePendingTossOrder } from "@/lib/pending-toss-order"
import { hrefWithLocale, getLocaleFromPathname } from "@/lib/i18n"

function newOrderId(): string {
  const uuid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "")
      : String(Date.now()) + Math.random().toString(36).slice(2)
  const raw = `pm_${uuid}`
  return raw.slice(0, 64)
}

function buildOrderName(titles: string[]): string {
  if (titles.length === 0) return "프롬프트 구매"
  if (titles.length === 1) return titles[0].slice(0, 100)
  return `${titles[0].slice(0, 40)} 외 ${titles.length - 1}건`
}

export default function CheckoutClient() {
  const router = useRouter()
  const pathname = usePathname()
  const locale = getLocaleFromPathname(pathname)
  const searchParams = useSearchParams()

  const promptId = searchParams.get("promptId")
  const itemsParam = searchParams.get("items")

  const lineItems = useMemo(() => {
    const ids = new Set<string>()
    if (promptId) ids.add(promptId)
    if (itemsParam) {
      itemsParam
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((id) => ids.add(id))
    }
    const list = promptSummaries.filter((p) => ids.has(p.id))
    return list
  }, [promptId, itemsParam])

  const totalAmount = useMemo(() => lineItems.reduce((sum, p) => sum + p.price, 0), [lineItems])

  const clientKey = process.env.NEXT_PUBLIC_TOSS_PAYMENTS_CLIENT_KEY

  const [sdkReady, setSdkReady] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const [payError, setPayError] = useState<string | null>(null)
  const paymentRef = useRef<ReturnType<Awaited<ReturnType<typeof loadTossPayments>>["payment"]> | null>(null)
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (!clientKey) {
      setInitError("NEXT_PUBLIC_TOSS_PAYMENTS_CLIENT_KEY 가 .env 에 설정되어 있지 않습니다.")
      return
    }
    if (lineItems.length === 0 || totalAmount < 1) {
      return
    }

    cancelledRef.current = false
    setInitError(null)
    setSdkReady(false)
    paymentRef.current = null

    ;(async () => {
      try {
        const tossPayments = await loadTossPayments(clientKey)
        if (cancelledRef.current) return
        paymentRef.current = tossPayments.payment({ customerKey: ANONYMOUS })
        setSdkReady(true)
      } catch (e) {
        console.error(e)
        if (!cancelledRef.current) {
          setInitError("토스페이먼츠 SDK를 불러오지 못했습니다. API 개별 연동 키의 클라이언트 키를 확인해 주세요.")
        }
      }
    })()

    return () => {
      cancelledRef.current = true
      paymentRef.current = null
      setSdkReady(false)
    }
  }, [clientKey, lineItems, totalAmount])

  const handlePay = useCallback(async () => {
    setPayError(null)
    const payment = paymentRef.current
    if (!payment || !sdkReady) {
      setPayError("결제 준비 중입니다. 잠시 후 다시 시도해 주세요.")
      return
    }

    const orderId = newOrderId()
    savePendingTossOrder({
      orderId,
      amount: totalAmount,
      promptIds: lineItems.map((p) => p.id),
    })

    const { successUrl, failUrl } = getPaymentReturnUrls(window.location.origin, pathname)
    const orderName = buildOrderName(lineItems.map((p) => p.title))

    try {
      await payment.requestPayment({
        method: "CARD",
        amount: {
          currency: "KRW",
          value: totalAmount,
        },
        orderId,
        orderName,
        successUrl,
        failUrl,
        customerName: "구매자",
        customerEmail: "buyer@example.com",
        card: {
          useEscrow: false,
          flowMode: "DEFAULT",
          useCardPoint: false,
          useAppCardOnly: false,
        },
      })
    } catch (e) {
      console.error(e)
      setPayError("결제창을 열 수 없습니다. 다시 시도해 주세요.")
    }
  }, [sdkReady, totalAmount, lineItems, pathname])

  const homeHref = hrefWithLocale(locale, "/")

  if (!promptId && !itemsParam) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center max-w-lg">
          <h1 className="text-2xl font-bold mb-4">결제할 상품이 없습니다</h1>
          <p className="text-muted-foreground mb-8">주문 정보가 올바르지 않습니다. 장바구니나 상품 페이지에서 다시 시도해 주세요.</p>
          <Button asChild>
            <Link href={homeHref}>홈으로</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (lineItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center max-w-lg">
          <h1 className="text-2xl font-bold mb-4">상품을 찾을 수 없습니다</h1>
          <p className="text-muted-foreground mb-8">선택한 프롬프트 ID가 목록에 없습니다.</p>
          <Button asChild>
            <Link href={hrefWithLocale(locale, "/prompts")}>프롬프트 목록</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <button
          type="button"
          onClick={() => router.back()}
          className="text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          ← 이전으로
        </button>

        <h1 className="text-2xl font-bold mb-6">결제</h1>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>주문 상품</CardTitle>
            <CardDescription>아래 버튼을 누르면 토스페이먼츠 결제창(카드·간편결제)이 열립니다.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lineItems.map((item) => (
              <div key={item.id} className="flex justify-between gap-4 text-sm">
                <span className="line-clamp-2">{item.title}</span>
                <span className="shrink-0 font-medium">₩{item.price.toLocaleString()}</span>
              </div>
            ))}
            <Separator />
            <div className="flex justify-between font-semibold text-lg">
              <span>총 결제 금액</span>
              <span>₩{totalAmount.toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        {initError ? <p className="text-destructive text-sm mb-4">{initError}</p> : null}

        {payError ? <p className="text-destructive text-sm mb-4">{payError}</p> : null}

        <Button className="w-full" size="lg" type="button" disabled={!sdkReady || !!initError} onClick={handlePay}>
          {sdkReady ? "결제하기" : "결제 준비 중…"}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          API 개별 연동 키(클라이언트·시크릿)를 사용합니다. 테스트 키로는 실제 출금이 되지 않습니다.
        </p>
      </div>
    </div>
  )
}
